import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import axios, { AxiosError } from "axios";

// Api
import { GetCategory } from "../../../../Api/Category";
import { GetMixedContent } from "../../../../Api/MixedContent";

// Models
import type ICarouselContentDTO from "../../../../Models/DTOs/ICarouselContentDTO";
import type ICategoryDTO from "../../../../Models/DTOs/ICategoryDTO";
import type { MixedContentItemDTO } from "../../../../Models/DTOs/CountWrapper";

// Helpers
import { transformMixedItemToCarouselContent } from "../../../../Helpers/DataConversion/ConvertToCarouselContentItem";
import { SetTitle } from "../../../../Helpers/PageMetadata";

// Components
import VideoContentGridLandscape from "../../../../Components/Layouts/Content/Watch/VideoFeed/VideoContentGridLandscape";

// Constants / Context
import { RoutePaths } from "../../../../Constants/RoutePaths";
import { UserAuthenticationContext } from "../../../../Context/UserAuthenticationContext";
import ButtonPill from "../../../../Components/Buttons/ButtonPill";
import IconArrowBack from "../../../../Assets/SVGs/Icons/UI/ArrowBack";

function dedupeMixed(items: ICarouselContentDTO[]) {
    const seen = new Set<string>();
    const out: ICarouselContentDTO[] = [];

    for (const it of items) {
        const key = `${it.ContentType}-${it.Id}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(it);
    }

    return out;
}

function CategoryScreen() {
    const navigate = useNavigate();
    const params = useParams<{ id: string }>();

    const TAKE = 50;
    const [skip, setSkip] = useState(0);

    const [category, setCategory] = useState<ICategoryDTO | null>(null);
    const [content, setContent] = useState<ICarouselContentDTO[]>([]);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const lastRequestedSkipRef = useRef<number | null>(null);
    const [done, setDone] = useState(false);

    const handleBack = () => {
        // window.history.length is crude, but good enough to avoid "nothing happens"
        if (window.history.length > 1) {
            navigate(-1);
            return;
        }
        navigate(RoutePaths.Browse);
    };

    // validate + load category once per id/user
    useEffect(() => {

        const idNum = Number(params.id);
        if (!params.id || isNaN(idNum) || idNum <= 0) {
            navigate(RoutePaths.Browse);
            return;
        }

        const controller = new AbortController();

        setCategory(null);
        setContent([]);
        setSkip(0);
        setDone(false);
        setError("");
        setLoading(true);

        (async () => {
            try {
                const catResult = await GetCategory(idNum, controller);
                if (!catResult || catResult instanceof AxiosError) {
                    navigate(RoutePaths.Browse);
                    return;
                }

                if (controller.signal.aborted) return;

                setCategory(catResult);
                SetTitle(catResult.Title);
            } catch (e: any) {
                if (e?.name === "CanceledError") return;
                console.error("Category fetch error:", e);
                toast.error("Failed to load category.");
                navigate(RoutePaths.Browse);
            } finally {
                if (!controller.signal.aborted) setLoading(false);
            }
        })();

        return () => controller.abort();
    }, [params.id, navigate]);

    // page mixed content (SeriesFeed-style)
    useEffect(() => {
        if (!category) return;
        if (done) return;

        const categoryId = Number(params.id);
        if (!categoryId || isNaN(categoryId) || categoryId <= 0) return;

        // StrictMode / sanity guard: don't request same skip twice
        if (lastRequestedSkipRef.current === skip) return;
        lastRequestedSkipRef.current = skip;

        const controller = new AbortController();

        setLoading(true);
        setError("");

        (async () => {
            try {
                const data = await GetMixedContent({
                    categoryId,
                    take: TAKE,
                    skip,
                    order: "desc",
                    publishedOnly: true,
                    signal: controller.signal,
                });

                if (controller.signal.aborted) return;

                const items: MixedContentItemDTO[] = data.items ?? [];
                const transformed = items
                    .map(transformMixedItemToCarouselContent)
                    .filter((x): x is ICarouselContentDTO => x !== null);

                setContent((prev) => dedupeMixed([...prev, ...transformed]));

                // stop when the API returns fewer than TAKE
                if (items.length !== TAKE) {
                    setDone(true);
                    return;
                }

                // next page
                setSkip((prevSkip) => prevSkip + TAKE);
            } catch (e: any) {
                if (e?.name === "CanceledError") return;
                if (axios.isCancel?.(e)) return;

                console.error("Category content fetch error:", e);
                setError("Failed to load category content.");
            } finally {
                if (!controller.signal.aborted) setLoading(false);
            }
        })();

        return () => controller.abort();
    }, [category, done, skip, params.id]);

    // if we finished and found nothing, bounce
    useEffect(() => {
        if (!done) return;
        if (content.length > 0) return;

        toast.error("No content exists for this category.");
        navigate(RoutePaths.Browse);
    }, [done, content.length, navigate]);

    const gridIsLoading = content.length === 0 && loading;

    return (
        <div className="page page--category">
            <div className="page__header">
                <h1>Category: {category?.Title ?? "Loading"}</h1>

                <ButtonPill
                    aria-label="Back"
                    icon={IconArrowBack()}
                    onClick={handleBack}
                    label="Back"
                />
            </div>

            <VideoContentGridLandscape
                isDarkMode={true}
                carouselItems={content}
                isLoading={gridIsLoading}
                backgroundHex="#151515"
            />
        </div>
    );
}

export default CategoryScreen;
